import { useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { AppContext } from '@rabbit/app-context';
import PrivateRoutes from '../utils/PrivateRoutes';
import PublicRoutes from '../utils/PublicRoutes';

// Views
import SignupView from '../views/SignupView';
import LoginView from '../views/LoginView';
import LiveClaimsView from '../views/LiveClaimsView';
import AccountManagementView from '../views/AccountManagementView';
import ClaimDetailView from '../views/ClaimDetailView';
import RegistrationsView from '../views/RegistrationsView';
import RegistrationsViewV2 from '../views/RegistrationsViewV2';
import VendableDetailsView from '../views/VendableDetailsView';
import PartsDetailView from '../views/PartsDetailView';
import CustomerDetailView from '../views/CustomerDetailView';
import PartnerDetailView from '../views/PartnerDetailView';
import AccessDeniedView from '../views/AccessDeniedView';
import { UserGroupView } from '../views/UserGroupView';
import PrivacyPolicyView from '../views/PrivacyPolicyView';
import TermsAndConditionsView from '../views/TermsAndConditionsView';
import ForgotPasswordView from '../views/ForgotPasswordView';
import PasswordResetView from '../views/PasswordResetView';
import RegistrationsDetailsView from '../views/RegistrationsDetailsView';
import PlatformServicesAgreementView, {
  PlatformServicesAgreementSmartView,
} from '../views/PlatformServicesAgreementView';
import { NotFoundView } from '@rabbit/elements/shared-components';
import { ManageView } from '../views/ManageView';
import MyOrganizationView from '../views/MyOrganizationView';
import RegistrationsPrintSummaryView from '../views/RegistrationsPrintSummaryView';
import { PlansAndPricesView } from '../views/PlansAndPricesView';
import WarrantyOverviewView from '../views/WarrantyOverviewView';
import DashboardView from '../views/DashboardView';
import RegisterProductInstaller from '@rabbit/sage/views/RegisterProductInstaller.tsx';
import PricingView from '../views/PricingView';
import ClaimDetailPrintWrapper from '../views/ClaimDetailPrintWrapper';
import RegistrationsRedirectHandler from '../views/RegistrationsRedirectHandler';

function AppRoutes() {
  const { config } = useContext(AppContext);
  const customDefaultPath = config?.NAVIGATION?.SAGE?.DEFAULT_PATH;

  return (
    <Routes>
      {/* private routes */}
      <Route element={<PrivateRoutes />}>
        <Route
          path={SAGE_ROUTE_NAME.DEFAULT}
          element={
            <Navigate to={customDefaultPath || SAGE_ROUTE_NAME.REGISTRATIONS} />
          }
        />
        <Route path={SAGE_ROUTE_NAME.DASHBOARD} element={<DashboardView />} />
        <Route path={SAGE_ROUTE_NAME.CLAIMS} element={<LiveClaimsView />} />
        <Route path={SAGE_ROUTE_NAME.MY_CLAIMS} element={<LiveClaimsView />} />
        <Route
          path={SAGE_ROUTE_NAME.CLAIMS + '/:id'}
          element={<ClaimDetailView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.CLAIMS + '/:id/print'}
          element={<ClaimDetailPrintWrapper />}
        />
        {/* `/registrations` serves only as a redirect, since it was renamed to `/legacy-registrations` for all tenants — Ref: SA-292 */}
        <Route
          path="/registrations"
          element={<RegistrationsRedirectHandler />}
        />
        <Route
          path={SAGE_ROUTE_NAME.REGISTRATIONS_BETA}
          element={<RegistrationsViewV2 />}
        />
        <Route
          path={SAGE_ROUTE_NAME.REGISTRATIONS_V2}
          element={<RegistrationsViewV2 />}
        />
        <Route path={SAGE_ROUTE_NAME.REGISTRATIONS} element={<RegistrationsView />} />
        <Route
          path={SAGE_ROUTE_NAME.REGISTRATIONS + '/:id'}
          element={<RegistrationsDetailsView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.REGISTRATIONS + '/:id/print'}
          element={<RegistrationsPrintSummaryView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.WARRANTY_OVERVIEW}
          element={<WarrantyOverviewView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.WARRANTY_OVERVIEW + '/:id'}
          element={<RegistrationsDetailsView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.WARRANTY_OVERVIEW + '/:id/print'}
          element={<RegistrationsPrintSummaryView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.PRODUCTS + '/:id'}
          element={<VendableDetailsView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.PRODUCTS_PARTS + '/:id'}
          element={<PartsDetailView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.MANAGE_ACCOUNT}
          element={<AccountManagementView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.MY_ORGANIZATION}
          element={<MyOrganizationView />}
        />
        <Route path={SAGE_ROUTE_NAME.MANAGE} element={<ManageView />} />
        <Route
          path={SAGE_ROUTE_NAME.MANAGE + '/customer/:id'}
          element={<CustomerDetailView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.MANAGE + '/partner/:id'}
          element={<PartnerDetailView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.PLANS_AND_PRICES}
          element={<PlansAndPricesView />}
        />
        <Route path={SAGE_ROUTE_NAME.PRICING} element={<PricingView />} />
        <Route path={SAGE_ROUTE_NAME.USER_GROUP} element={<UserGroupView />} />
        {/*
            These routes are duplicated as they can be accessed whether logged in or not.
            This allows the navbar to be displayed with the proper data in both states.
            And it avoids a redirect back to dashboard when logged in.
            This can most likely be optimized but is more than good enough for the foreseeable future. - DC
          */}
        <Route
          path={SAGE_ROUTE_NAME.PRIVACY_POLICY}
          element={<PrivacyPolicyView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.TERMS_AND_CONDITIONS}
          element={<TermsAndConditionsView />}
        />
      </Route>

      {/* public routes */}
      <Route element={<PublicRoutes />}>
        <Route path={SAGE_ROUTE_NAME.LOGIN} element={<LoginView />} />
        <Route path={SAGE_ROUTE_NAME.SIGNUP} element={<SignupView />} />
        <Route
          path={SAGE_ROUTE_NAME.FORGOT_PASSWORD}
          element={<ForgotPasswordView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.CONFIRM_EMAIL}
          element={<PasswordResetView step={1} />}
        />
        <Route
          path={SAGE_ROUTE_NAME.RESET_PASSWORD}
          element={<PasswordResetView step={2} />}
        />
        <Route
          path={SAGE_ROUTE_NAME.TERMS_AND_CONDITIONS}
          element={<TermsAndConditionsView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.PLATFORM_SERVICES_AGREEMENT}
          element={<PlatformServicesAgreementSmartView />}
          
        />
        <Route
          path={SAGE_ROUTE_NAME.PLATFORM_SERVICES_AGREEMENT_OLD}
          element={<PlatformServicesAgreementView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.PRIVACY_POLICY}
          element={<PrivacyPolicyView />}
        />
        <Route
          path={SAGE_ROUTE_NAME.REGISTER_PRODUCT_INSTALLER}
          element={<RegisterProductInstaller />}
        />
      </Route>

      {/* misc routes */}
      <Route
        path={SAGE_ROUTE_NAME.ACCESS_DENIED}
        element={<AccessDeniedView />}
      />
      <Route path="*" element={<NotFoundView />} />
    </Routes>
  );
}

export default AppRoutes;
